<script>
import { clickOutsideMixin } from '@utils/click-outside-mixin'

/**
 * BaseActionsMenu — botão "Mais ações" (wrapper do BaseButton, mesmo visual do
 * botão de filtros: borda + chevron) que abre um dropdown com a lista de ações.
 *
 * Pensado para o slot `actions` da navegação (BaseSegmentedTabs) das views.
 *
 * Props:
 *  - label: texto do botão (default "Mais ações").
 *  - actions: [{ label, onClick, icon?, disabled?, danger?, description?,
 *    dataTour? }] —
 *    itens do menu. `icon` é o nome de um icon-svg à esquerda do label.
 *    `danger: true` deixa o item vermelho com um divisor acima (ação
 *    destrutiva, ex: Excluir). `description` é uma legenda curta abaixo do
 *    label (fonte menor, cor neutra) — pensada para resumir em poucas
 *    palavras por que uma ação está desabilitada, sem inflar o item com uma
 *    frase inteira. `dataTour` (opcional) vira o atributo `data-tour` do
 *    item, nos dois grupos (comum e destrutivo) — âncora do tour guiado da
 *    Jornada, que precisa apontar um item específico do menu (ex.: "Ver IPS"
 *    na Visão Geral do Compliance). Sem o campo, o item não ganha atributo.
 * Também aceita conteúdo custom via slot default (dentro do menu).
 */
// Cor institucional de icone de acao em tabela/menu. Era literal em 4 pontos
// deste arquivo; uma constante evita que um deles saia de sincronia numa troca
// futura de token.
export const COR_ICONE_ACAO = '#8E8E8E'

export default {
  name: 'BaseActionsMenu',
  mixins: [clickOutsideMixin],
  props: {
    label: {
      type: String,
      default: 'Mais ações',
    },
    actions: {
      type: Array,
      default: () => [],
    },
    menuWidth: {
      type: [String, Number],
      default: 220,
    },
    // Trigger só-ícone (3 pontinhos) em vez do botão com label — p/ ações de
    // linha de tabela.
    iconOnly: {
      type: Boolean,
      default: false,
    },
    // Abre o menu ACIMA do ativador em vez de abaixo — para ativadores perto
    // do fim da viewport/container com scroll (ex.: última linha de uma
    // tabela), onde abrir para baixo cortaria o menu.
    top: {
      type: Boolean,
      default: false,
    },
    keepOpenOnContentClick: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      COR_ICONE_ACAO,
      menu: false,
    }
  },
  computed: {
    // Alguma ação em loading → mostra spinner no botão (no lugar do chevron).
    isLoading() {
      return this.actions.some((action) => action.loading)
    },
    regularActions() {
      return this.actions.filter((action) => !action.danger)
    },
    destructiveActions() {
      return this.actions.filter((action) => action.danger)
    },
    // Classe única por instância — usada pelo listener manual abaixo pra
    // achar SÓ o conteúdo deste menu (o conteúdo do v-menu é teleportado
    // pro <body>, fora de this.$el).
    contentClass() {
      return `base-actions-menu__content base-actions-menu__content--${this._uid}`
    },
  },
  methods: {
    handleAction(action) {
      if (action.disabled || action.loading) return
      this.menu = false
      if (typeof action.onClick === 'function') {
        action.onClick()
      }
    },
    // Contrato do clickOutsideMixin: só enquanto isOpen() é true o listener
    // fica anexado ao document (59 call-sites de BaseActionsMenu na
    // plataforma — sem isso, cada um manteria um listener global vivo o
    // tempo todo, mesmo os que nunca abrem).
    isOpen() {
      return this.menu
    },
    // O fechamento nativo do v-menu ao clicar fora não é confiável dentro de
    // painéis flutuantes com position:fixed/absolute — o menu fica aberto
    // até selecionar um item. O clickOutsideMixin é rede de segurança: se o
    // v-menu já fechou sozinho, isso vira um no-op.
    isClickOutside(target) {
      if (!this.menu) return false
      const contentEl = document.querySelector(
        `.base-actions-menu__content--${this._uid}`
      )
      const clickedTrigger = this.$el && this.$el.contains(target)
      const clickedContent = contentEl && contentEl.contains(target)
      return !clickedTrigger && !clickedContent
    },
    onClickOutside() {
      this.menu = false
    },
  },
}
</script>

<template>
  <v-menu
    v-model="menu"
    offset-y
    left
    :top="top"
    :close-on-content-click="!keepOpenOnContentClick"
    :nudge-top="top ? 8 : 0"
    :nudge-bottom="top ? 0 : 8"
    transition="slide-y-transition"
    :content-class="contentClass"
    :min-width="menuWidth"
    :max-width="menuWidth"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-if="iconOnly"
        icon
        small
        :aria-label="label"
        v-bind="attrs"
        v-on="on"
      >
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          :size="16"
          :width="2"
          color="primary"
        />
        <icon-svg v-else name="mdi-dots-vertical" :size="18" :color="COR_ICONE_ACAO" />
      </v-btn>
      <BaseButton v-else bordered fitContent v-bind="attrs" v-on="on">
        {{ label }}
        <!-- Footprint fixo (18x18) para o botão não mudar de tamanho entre o
             chevron e o spinner de loading. -->
        <span class="base-actions-menu__trailing">
          <v-progress-circular
            v-if="isLoading"
            indeterminate
            :size="16"
            :width="2"
            color="primary"
          />
          <icon-svg
            v-else
            name="mdi-chevron-down"
            :size="18"
            :color="COR_ICONE_ACAO"
            class="base-actions-menu__chevron"
            :class="{ 'base-actions-menu__chevron--open': menu }"
          />
        </span>
      </BaseButton>
    </template>

    <div class="base-actions-menu__panel">
      <div v-if="regularActions.length" class="base-actions-menu__list">
        <button
          v-for="(action, index) in regularActions"
          :key="index"
          type="button"
          class="base-actions-menu__item"
          :disabled="action.disabled || action.loading"
          :data-tour="action.dataTour"
          @click="handleAction(action)"
        >
          <v-progress-circular
            v-if="action.loading"
            indeterminate
            :size="15"
            :width="2"
            :color="COR_ICONE_ACAO"
          />
          <!-- `action.iconColor` e OPCIONAL e o default segue a cor
               institucional de icone de acao: nenhum call-site existente muda.
               Serve para menus em que o icone precisa dizer a NATUREZA da acao
               (comprar / vender / trocar) — com seis icones cinza, o usuario
               tem de ler cada rotulo para achar o certo. A lista
               destrutiva abaixo segue fixa em `danger`, que e semantica do
               grupo e nao escolha do chamador. -->
          <icon-svg
            v-else-if="action.icon"
            :name="action.icon"
            :size="15"
            :color="action.iconColor || COR_ICONE_ACAO"
          />
          <span class="base-actions-menu__item-text">
            <span>{{ action.label }}</span>
            <span
              v-if="action.description"
              class="base-actions-menu__item-description"
            >
              {{ action.description }}
            </span>
          </span>
        </button>
      </div>

      <slot />

      <div
        v-if="destructiveActions.length"
        class="base-actions-menu__list base-actions-menu__list--danger"
      >
        <button
          v-for="(action, index) in destructiveActions"
          :key="index"
          type="button"
          class="base-actions-menu__item base-actions-menu__item--danger"
          :disabled="action.disabled || action.loading"
          :data-tour="action.dataTour"
          @click="handleAction(action)"
        >
          <v-progress-circular
            v-if="action.loading"
            indeterminate
            :size="15"
            :width="2"
            color="danger"
          />
          <icon-svg
            v-else-if="action.icon"
            :name="action.icon"
            :size="15"
            color="danger"
          />
          {{ action.label }}
        </button>
      </div>
    </div>
  </v-menu>
</template>

<style lang="sass" scoped>
.base-actions-menu__trailing
  width: 18px
  height: 18px
  display: inline-flex
  align-items: center
  justify-content: center
  flex-shrink: 0

.base-actions-menu__chevron
  transition: transform 0.15s ease

  &--open
    transform: rotate(180deg)

.base-actions-menu__panel
  background-color: #fff

.base-actions-menu__list
  padding: 4px

  &--danger:not(:first-child)
    padding-top: 4px
    border-top: 1px solid var(--border-hairline)

// Mesmo item do BaseSplitButton (az-split__item): botão flex com ícone+label
// alinhados ao centro via gap, garantindo alinhamento idêntico ao do design.
.base-actions-menu__item
  position: relative
  display: flex
  align-items: center
  gap: 8px
  width: 100%
  padding: 8px 10px
  font-family: Geist, sans-serif
  font-size: 13px
  color: var(--neutral-950)
  text-align: left
  cursor: pointer
  background: none
  border: 0
  border-radius: var(--radius-sm)

  &:hover
    background: var(--neutral-100)

  &:disabled
    cursor: not-allowed
    opacity: 0.6

  &--danger
    color: var(--danger-text)

.base-actions-menu__item-text
  display: flex
  flex-direction: column
  min-width: 0

.base-actions-menu__item-description
  font-size: 11px
  color: var(--neutral-600)
</style>

<style lang="sass">
// Conteúdo é teleportado ao body — mesmo arredondamento/borda do menu de filtros.
.v-menu__content.base-actions-menu__content
  border: 1px solid var(--border-hairline)
  border-radius: var(--radius-md)
  overflow: hidden
</style>
